import KpiChart from "@app/components/kpi-chart";
import ExplainButton from "@app/components/explain/explain-button";
import type { CohortDimension } from "@app/components/explain/explain-panel";
import { dateRangeFromISO, useDateRange } from "@app/lib/date-range";
import { useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type {
  explorePage_Query,
  CubeDimensionEnum,
} from "@app/queries/explorePage_Query.graphql";

// The /explore cube dimension → the cohort-retention dimension a segment scopes by.
// (Total view has no dimension → global retention.)
const COHORT_DIMENSION: Partial<Record<CubeDimensionEnum, CohortDimension>> = {
  PLAN_TIER: "PLAN_TIER",
  SEAT_BAND: "SEAT_BAND",
  COUNTRY: "COUNTRY",
  COHORT_MONTH: "COHORT",
  TENURE_BAND: "TENURE",
};

export const exploreQuery = graphql`
  query explorePage_Query(
    $groupBy: CubeDimensionEnum
    $seatBands: [String!]
    $from: String
  ) {
    cube(groupBy: $groupBy, seatBands: $seatBands, from: $from) {
      groupBy
      series {
        key
        monthly {
          month
          mrr
          arr
          activeLogos
          newLogos
          churnedLogos
          newMrr
          expansionMrr
          contractionMrr
          churnedMrr
          reactivationMrr
        }
      }
    }
  }
`;

type Dimension = { label: string; value: CubeDimensionEnum | null };
const DIMENSIONS: Dimension[] = [
  { label: "Total", value: null },
  { label: "Plan tier", value: "PLAN_TIER" },
  { label: "Seat band", value: "SEAT_BAND" },
  { label: "Country", value: "COUNTRY" },
  { label: "Cohort", value: "COHORT_MONTH" },
  { label: "Tenure", value: "TENURE_BAND" },
];

type CubePoint =
  explorePage_Query["response"]["cube"]["series"][number]["monthly"][number];
type Measure = {
  label: string;
  field: keyof CubePoint;
  format: "money" | "number";
};
const MEASURES: Measure[] = [
  { label: "MRR", field: "mrr", format: "money" },
  { label: "ARR", field: "arr", format: "money" },
  { label: "New MRR", field: "newMrr", format: "money" },
  { label: "Expansion", field: "expansionMrr", format: "money" },
  { label: "Contraction", field: "contractionMrr", format: "money" },
  { label: "Churned MRR", field: "churnedMrr", format: "money" },
  { label: "Reactivation", field: "reactivationMrr", format: "money" },
  { label: "Active logos", field: "activeLogos", format: "number" },
  { label: "New logos", field: "newLogos", format: "number" },
  { label: "Churned logos", field: "churnedLogos", format: "number" },
];

const SEAT_BANDS = ["1", "2-5", "6-10", "11+"];
const PALETTE = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#db2777",
  "#65a30d",
  "#ea580c",
  "#0d9488",
  "#9333ea",
  "#4b5563",
  "#1d4ed8",
  "#15803d",
  "#b45309",
  "#991b1b",
  "#6d28d9",
  "#155e75",
  "#9d174d",
  "#3f6212",
  "#9a3412",
  "#115e59",
  "#6b21a8",
  "#1f2937",
];

// Raw Stripe price nicknames include non-substantive buckets; hide them.
const JUNK_KEYS = new Set([
  "Unknown",
  "Past Due Invoices",
  "Past Due ZenMaid Invoice",
]);

export default function ExplorePage() {
  const range = useDateRange();
  const from = dateRangeFromISO(range) ?? undefined;

  const [dimension, setDimension] = useState<CubeDimensionEnum | null>(null); // Total, not a 41-way split
  const [measureIndex, setMeasureIndex] = useState(0); // MRR — readable on first load
  const [seatBands, setSeatBands] = useState<string[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const measure = MEASURES[measureIndex];

  const data = useLazyLoadQuery<explorePage_Query>(exploreQuery, {
    groupBy: dimension ?? undefined,
    seatBands: seatBands.length > 0 ? seatBands : undefined,
    from,
  });

  const series = data.cube.series.filter((s) => !JUNK_KEYS.has(s.key));

  const months = useMemo(() => {
    const set = new Set<string>();
    series.forEach((s) => s.monthly.forEach((p) => set.add(p.month)));
    return [...set].sort();
  }, [series]);

  const chartSeries = series
    .filter((s) => !hidden.has(s.key))
    .map((s, index) => {
      const byMonth = new Map(
        s.monthly.map((p) => [p.month, p[measure.field] as number])
      );
      return {
        name: s.key,
        type: "bar" as const,
        stack: "cube",
        color: PALETTE[colorIndex(series, s.key) % PALETTE.length],
        points: months.map((month) => ({
          month,
          value: byMonth.get(month) ?? 0,
        })),
      };
    });

  const toggleSeatBand = (band: string) =>
    setSeatBands((prev) =>
      prev.includes(band) ? prev.filter((b) => b !== band) : [...prev, band]
    );

  const toggleHidden = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div>
        <h1 className="text-xl font-bold">Explore</h1>
        <p className="text-sm text-gray-500">
          Slice MRR, ARR, expansion, churn and logos by any dimension. The date
          range in the header applies. Example: group by{" "}
          <span className="font-medium">Plan tier</span>, measure{" "}
          <span className="font-medium">Expansion</span>, filter seats{" "}
          <span className="font-medium">6-10</span>.
        </p>
      </div>

      <div className="space-y-3 bg-white rounded border p-4">
        <Control label="Group by">
          {DIMENSIONS.map((d) => (
            <Segment
              key={d.label}
              active={dimension === d.value}
              onClick={() => setDimension(d.value)}
            >
              {d.label}
            </Segment>
          ))}
        </Control>

        <Control label="Measure">
          {MEASURES.map((m, i) => (
            <Segment
              key={m.label}
              active={i === measureIndex}
              onClick={() => setMeasureIndex(i)}
            >
              {m.label}
            </Segment>
          ))}
        </Control>

        <Control label="Seats">
          {SEAT_BANDS.map((band) => (
            <Segment
              key={band}
              active={seatBands.includes(band)}
              onClick={() => toggleSeatBand(band)}
            >
              {band}
            </Segment>
          ))}
          {seatBands.length > 0 && (
            <button
              type="button"
              onClick={() => setSeatBands([])}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-900"
            >
              clear
            </button>
          )}
        </Control>
      </div>

      {months.length === 0 ? (
        <p className="text-gray-500">
          No Stripe data for this slice. Sync Stripe and rebuild the fact table.
        </p>
      ) : (
        <>
          <KpiChart
            title={`${measure.label}${
              dimension
                ? ` by ${DIMENSIONS.find(
                    (d) => d.value === dimension
                  )?.label.toLowerCase()}`
                : ""
            }`}
            series={
              chartSeries.length > 0
                ? chartSeries
                : [
                    {
                      name: "—",
                      type: "bar",
                      points: months.map((m) => ({ month: m, value: 0 })),
                    },
                  ]
            }
            format={measure.format}
            onMonthClick={() => {}}
          />

          {dimension && (
            <div className="flex flex-wrap gap-2">
              {series.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleHidden(s.key)}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${
                    hidden.has(s.key) ? "opacity-40" : ""
                  }`}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm"
                    style={{
                      background:
                        PALETTE[colorIndex(series, s.key) % PALETTE.length],
                    }}
                  />
                  {s.key}
                </button>
              ))}
            </div>
          )}

          <SummaryTable series={series} months={months} measure={measure} dimension={dimension} />
        </>
      )}
    </div>
  );
}

function colorIndex(
  series: explorePage_Query["response"]["cube"]["series"],
  key: string
): number {
  return series.findIndex((s) => s.key === key);
}

function Control({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-gray-500 w-16">{label}</span>
      <div className="flex items-center gap-1 flex-wrap">{children}</div>
    </div>
  );
}

function Segment({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded text-xs font-medium border ${
        active
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

// Per-segment retention explains (NRR / GRR / logo), scoped to one cube segment via the
// cohort decomposition. `dim` is null on the Total view → global (whole-book) retention.
function RetentionCell({ dim, value, label }: { dim: CohortDimension | null; value: string | null; label: string }) {
  const scope = dim ? { dimension: dim, value } : {};
  return (
    <div className="flex items-center justify-end gap-1">
      <ExplainButton metric="NRR" title={`NRR · ${label}`} label="NRR" {...scope} />
      <ExplainButton metric="GRR" title={`GRR · ${label}`} label="GRR" {...scope} />
      <ExplainButton metric="LOGO_RETENTION" title={`Logo retention · ${label}`} label="Logo" {...scope} />
    </div>
  );
}

function SummaryTable({
  series,
  months,
  measure,
  dimension,
}: {
  series: explorePage_Query["response"]["cube"]["series"];
  months: string[];
  measure: Measure;
  dimension: CubeDimensionEnum | null;
}) {
  const cohortDim = dimension ? COHORT_DIMENSION[dimension] ?? null : null;
  const latest = months[months.length - 1];
  const fmt = (value: number) =>
    measure.format === "money"
      ? value.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        })
      : value.toLocaleString();

  const rows = series
    .map((s) => ({
      key: s.key,
      value:
        (s.monthly.find((p) => p.month === latest)?.[
          measure.field
        ] as number) ?? 0,
    }))
    .sort((a, b) => b.value - a.value);

  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <div className="bg-white rounded border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Group</th>
            <th className="text-right px-3 py-2 font-medium">
              {measure.label} ({latest})
            </th>
            <th className="text-right px-3 py-2 font-medium">Retention (TTM)</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="px-3 py-1.5">{row.key}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {fmt(row.value)}
              </td>
              <td className="px-3 py-1.5">
                {cohortDim && <RetentionCell dim={cohortDim} value={row.key} label={row.key} />}
              </td>
            </tr>
          ))}
          <tr className="font-medium border-t-2">
            <td className="px-3 py-1.5">Total</td>
            <td className="px-3 py-1.5 text-right tabular-nums">
              {fmt(total)}
            </td>
            <td className="px-3 py-1.5">
              <RetentionCell dim={null} value={null} label="All customers" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
